import { useMemo } from 'react'
import { useApplicant } from '~/api/queries/applicant'
import { Dropdown } from '~/components/Form/Input/Dropdown'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import type { TaxonomyItem } from '~/domains/taxonomies/types'
import { applicantStatusArchivedId } from '~/domains/taxonomies/types'
import { toast } from '~/utils'
import { useUpdateApplicantStatusMutation } from '../mutations/updateApplicantStatus'

const ARCHIVE_TYPE_PREFIX = 'archived:'

type ApplicantStatusUpdaterProps = {
  applicantId: number
  ghost?: boolean
  initialStatus?: TaxonomyItem<'statuses'>
}

export const ApplicantStatusUpdater = ({
  applicantId,
  ghost,
  initialStatus
}: ApplicantStatusUpdaterProps) => {
  const { mutate: updateApplicantStatusMutate, isPending } =
    useUpdateApplicantStatusMutation(applicantId)
  const { data: statuses, isLoading: isStatusesLoading } =
    useTaxonomy('statuses')
  const { data: archiveTypes } = useTaxonomy('archive_types')
  // Skip per-row GET /v1/applicants/{id} when the table/modal already
  // seeded status — avoids N+1 on /admin/candidats/search (JAVASCRIPT-REACT-2YS).
  const { data: applicant, isLoading: isApplicantLoading } = useApplicant({
    id: applicantId,
    enabled: !initialStatus
  })

  const candidateArchiveTypes =
    archiveTypes?.filter((t) => t.type === 'candidat') ?? []

  const options = useMemo(
    () =>
      statuses?.map((status) => ({
        label: status.name,
        value: String(status.id),
        element: <Badge title={status.name} backgroundColor={status.color} />,
        children:
          status.id === applicantStatusArchivedId
            ? candidateArchiveTypes.map((t) => ({
                label: t.name,
                value: `${ARCHIVE_TYPE_PREFIX}${t.id}`
              }))
            : undefined
      })) ?? [],
    [statuses, candidateArchiveTypes]
  )

  const effectiveStatusId = applicant?.status?.id ?? initialStatus?.id
  const fallbackOption = useMemo(
    () =>
      initialStatus
        ? {
            label: initialStatus.name,
            value: String(initialStatus.id),
            element: (
              <Badge
                title={initialStatus.name}
                backgroundColor={initialStatus.color}
              />
            )
          }
        : undefined,
    [initialStatus]
  )
  const defaultValueOption = useMemo(
    () =>
      options.find((o) => o.value === String(effectiveStatusId)) ??
      fallbackOption,
    [options, effectiveStatusId, fallbackOption]
  )
  const isStillResolvingStatus =
    !defaultValueOption && (isApplicantLoading || isStatusesLoading)

  const handleChange = (value: string) => {
    const isArchiveChild = value.startsWith(ARCHIVE_TYPE_PREFIX)
    const statusId = isArchiveChild ? applicantStatusArchivedId : Number(value)
    const status = statuses?.find((s) => s.id === statusId)
    if (!status) return
    if (!isArchiveChild && effectiveStatusId === status.id) return

    if (status.id === applicantStatusArchivedId && !isArchiveChild) {
      toast(
        'Raison requise',
        "Veuillez sélectionner une raison d'archivage.",
        ToastType.Danger
      )
      return
    }

    const archiveTypeId = isArchiveChild
      ? Number(value.slice(ARCHIVE_TYPE_PREFIX.length))
      : undefined
    const archiveType =
      archiveTypeId !== undefined
        ? candidateArchiveTypes.find((t) => t.id === archiveTypeId)
        : undefined

    updateApplicantStatusMutate(
      {
        status,
        archiveType
      },
      {
        onSuccess: () => {
          // Update v2 applicant infos
          window.applicant?.refreshInfos?.(applicantId)
        },
        onError: (e) => {
          if (e.response?.message?.includes('already exists with')) {
            // TODO: find a better way to handle backend validation errors
            toast(
              'Echec de la mise à jour du statut',
              "Ce candidat est un doublon, il ne peut être mis à jour tant que son doublon n'est pas archivé.",
              ToastType.Danger
            )
            return
          }
          toast(
            'Echec de la mise à jour du statut',
            'Impossible de mettre à jour le statut du candidat.',
            ToastType.Danger
          )
        }
      }
    )
  }

  if (isStillResolvingStatus) {
    return <Shimmer height={24} width={110} />
  }

  return (
    <Dropdown
      options={options}
      defaultValueOption={defaultValueOption}
      onChange={handleChange}
      isUpdating={isPending}
      required
      extraSmall
      placeholder="Changer le statut"
      childrenAsSubmenu
      ghost={ghost}
    />
  )
}
